import {useMutation} from '@apollo/react-hooks';

import type {Permission} from 'nutshell-graphql-types';
import type {
    UpdateRolePermission as UpdateRolePermissionMutation,
    UpdateRolePermissionVariables as UpdateRolePermissionMutationVariables,
    RoleUpdatePermissionInput,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../../../apollo-client';

import ROLE_FRAGMENT from '../fragments/role-fragment.graphql';
import UPDATE_ROLE_PERMISSION from '../mutations/update-role-permission.graphql';

export function useUpdateRolePermission(): {
    updateRolePermission: (arg1: RoleUpdatePermissionInput) => Promise<any>;
    isUpdating: boolean;
} {
    const [updateRolePermission, {loading}] = useMutation<
        UpdateRolePermissionMutation,
        UpdateRolePermissionMutationVariables
    >(UPDATE_ROLE_PERMISSION);

    return {
        updateRolePermission: (input: RoleUpdatePermissionInput) => {
            const roleFragment = nutApolloClient.readFragment({
                id: input.roleId,
                fragment: ROLE_FRAGMENT,
            });

            return updateRolePermission({
                variables: {input},
                optimisticResponse: roleFragment
                    ? {
                          // @ts-expect-error
                          __typename: 'RoleUpdatePermissionPayload',
                          roleUpdatePermission: {
                              __typename: 'RoleUpdatePermissionPayload',
                              role: {
                                  ...roleFragment,
                                  __typename: 'Role',
                                  permissions:
                                      input.action === 'add'
                                          ? [...roleFragment.permissions, input.permission]
                                          : roleFragment.permissions.filter(
                                                (permission: Permission) =>
                                                    permission !== input.permission
                                            ),
                              },
                          },
                      }
                    : undefined,
                update: (proxy, mutationResults) => {
                    const updatedRole =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.roleUpdatePermission &&
                        mutationResults.data.roleUpdatePermission.role;

                    if (updatedRole) {
                        proxy.writeFragment({
                            id: input.roleId,
                            fragment: ROLE_FRAGMENT,
                            data: updatedRole,
                        });
                    }
                },
            });
        },
        isUpdating: loading,
    };
}
